import { createFileRoute, Link } from "@tanstack/react-router";
import type { ServiceCategory, ServiceItem } from "@/lib/services";
import { ProvincePageShell } from "@/components/ProvincePageShell";
import { pageHeadForProvince } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildServiceListSchema, buildSpeakableSchema } from "@/lib/schema";
import { getActiveProvince } from "@/provinces/_registry";
import { Globe2, ChevronRight } from "lucide-react";

const MARRIAGE_DESTINATIONS = [
  { slug: "marriage-denmark", flag: "🇩🇰", title: "Denmark", sub: "Copenhagen / Ærø — 3-5 วัน" },
  { slug: "marriage-las-vegas", flag: "🇺🇸", title: "Las Vegas", sub: "Clark County — 1 วัน" },
  { slug: "marriage-hongkong", flag: "🇭🇰", title: "Hong Kong", sub: "Civil Celebrant — 15 วัน" },
  { slug: "marriage-japan", flag: "🇯🇵", title: "Japan", sub: "Kon-in Todoke — 1 วัน" },
  { slug: "marriage-uk", flag: "🇬🇧", title: "United Kingdom", sub: "Notice of Marriage — 29 วัน" },
  { slug: "marriage-germany", flag: "🇩🇪", title: "Germany", sub: "Standesamt + OLG — 3-6 เดือน" },
  { slug: "marriage-new-zealand", flag: "🇳🇿", title: "New Zealand", sub: "BDM Licence — 3 วัน" },
  { slug: "marriage-singapore", flag: "🇸🇬", title: "Singapore", sub: "ROM — 21 วัน" },
  { slug: "marriage-australia", flag: "🇦🇺", title: "Australia", sub: "NOIM + PMV 300" },
  { slug: "marriage-france", flag: "🇫🇷", title: "France", sub: "Mairie + Bans 10 วัน" },
  { slug: "marriage-netherlands", flag: "🇳🇱", title: "Netherlands", sub: "Gemeente + Ondertrouw" },
  { slug: "marriage-sweden", flag: "🇸🇪", title: "Sweden", sub: "Skatteverket + Hindersprövning" },
  { slug: "marriage-norway", flag: "🇳🇴", title: "Norway", sub: "Skatteetaten + Prøving" },
  { slug: "marriage-switzerland", flag: "🇨🇭", title: "Switzerland", sub: "Zivilstandsamt + สัมภาษณ์" },
  { slug: "marriage-taiwan", flag: "🇹🇼", title: "Taiwan", sub: "Household Office — 1 วัน" },
  { slug: "marriage-korea", flag: "🇰🇷", title: "Korea", sub: "Gu Office + F-6 Visa" },
];

export const Route = createFileRoute("/services/")({
  loader: async (): Promise<{
    services: ServiceItem[];
    categories: { id: string; label: string; sub?: string }[];
  }> => {
    const { SERVICES, CATEGORIES } = await import("@/lib/services");
    return { services: SERVICES, categories: CATEGORIES };
  },
  head: ({ loaderData }) => {
    const { config } = getActiveProvince();
    const base = pageHeadForProvince({
      title: `บริการทั้งหมด | Thai Notary ${config.nameEn}`,
      description: `บริการครบวงจรใน${config.nameTh}: Notary Public, แปลทุกภาษา, รับรองกงสุล MFA, รับรองสถานทูต, ตรวจประวัติอาชญากร, จดทะเบียนสมรสไทย–ต่างชาติ, วีซ่าและ Work Permit`,
      path: "/services",
      alternates: [{ lang: "en", path: "/en/services" }],
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/services")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "บริการทั้งหมด", url: "/services" },
            ]),
          ),
        },
        ...buildServiceListSchema(
          (loaderData?.services ?? []).map((s) => ({
            name: s.title,
            description: s.desc,
            url: `/services/${s.slug}`,
            priceFrom: s.priceFrom,
          })),
        ).map((s) => ({ type: "application/ld+json", children: JSON.stringify(s) })),
      ],
    };
  },
  component: ServicesIndex,
});

function ServicesIndex() {
  const { config } = getActiveProvince();
  const { services: SERVICES, categories: CATEGORIES } = Route.useLoaderData() as {
    services: ServiceItem[];
    categories: { id: string; label: string; sub?: string }[];
  };
  return (
    <ProvincePageShell
      eyebrow="All Services"
      title="บริการทั้งหมดของเรา"
      subtitle={`Notary Public, แปลเอกสารทุกภาษา, รับรองกงสุล MFA, สถานทูต, ตรวจประวัติ, จดทะเบียนสมรสไทย–ต่างชาติ, วีซ่าและ Work Permit — ครบจบในที่เดียวใน${config.nameTh}`}
      breadcrumb={[{ label: "Home", to: "/" }, { label: "บริการทั้งหมด" }]}
    >
      <div className="mx-auto max-w-7xl space-y-14 px-4 py-16">
        {/* Quick category jump nav */}
        <nav className="flex flex-wrap gap-2 rounded-2xl border border-border bg-card p-4">
          <span className="text-[11px] font-bold uppercase tracking-widest text-gold mr-2 self-center">หมวด</span>
          {CATEGORIES.map((cat) => (
            <a key={cat.id} href={`#cat-${cat.id}`} className="rounded-md border border-border bg-secondary/60 px-3 py-1.5 text-xs font-bold text-primary hover:border-gold hover:text-gold">
              {cat.label}
            </a>
          ))}
          <a href="#marriage-destinations" className="rounded-md border border-gold/40 bg-gold/10 px-3 py-1.5 text-xs font-bold text-gold hover:bg-gold hover:text-primary">
            ✦ Marriage Destinations
          </a>
        </nav>

        {CATEGORIES.map((cat) => {
          const list = SERVICES.filter((s) => s.category === (cat.id as ServiceCategory));
          return (
            <section key={cat.id} id={`cat-${cat.id}`} className="scroll-mt-24">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-gold">{cat.sub}</div>
                <h2 className="mt-1 text-2xl font-black text-primary">{cat.label}</h2>
              </div>
              <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {list.map((s) => (
                  <Link key={s.id} to="/services/$service" params={{ service: s.slug }} className="group rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 hover:border-gold">
                    <h3 className="text-base font-bold text-primary group-hover:text-gold">{s.title}</h3>
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gold">{s.titleEn}</div>
                    <p className="mt-2 text-xs text-muted-foreground">{s.desc}</p>
                    {s.priceFrom && <div className="mt-3 text-sm font-bold text-gold">เริ่ม {s.priceFrom.toLocaleString()} บาท{s.priceTo ? ` – ${s.priceTo.toLocaleString()}` : ""}</div>}
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        {/* Featured: Marriage Destinations */}
        <section id="marriage-destinations" className="scroll-mt-24 rounded-3xl border-2 border-gold/40 bg-gradient-to-br from-gold/5 via-card to-card p-8">
          <div className="flex items-center gap-3">
            <Globe2 className="h-6 w-6 text-gold" />
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-gold">Marriage Abroad • {config.nameEn} Couples</div>
              <h2 className="mt-1 text-2xl font-black text-primary">เลือกประเทศที่ต้องการจดทะเบียนสมรส</h2>
              <p className="mt-1 text-sm text-muted-foreground">บริการ End-to-End จาก{config.nameTh} — เตรียมเอกสาร, MFA, Embassy, ประสานเทศบาล, บันทึก คร.22 หลังกลับไทย</p>
            </div>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {MARRIAGE_DESTINATIONS.map((d) => (
              <Link key={d.slug} to="/services/$service" params={{ service: d.slug }} className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-gold">
                <div className="text-3xl">{d.flag}</div>
                <h3 className="mt-2 text-base font-black text-primary group-hover:text-gold">{d.title}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{d.sub}</p>
                <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold text-gold">ดูรายละเอียด <ChevronRight className="h-3 w-3" /></div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </ProvincePageShell>
  );
}